<template>
  <v-container fluid>
    <v-expansion-panels hover flat>
      <v-expansion-panel>
        <v-expansion-panel-header class='expansion-panel-header-dense'>
          <h2>Site types</h2>
        </v-expansion-panel-header>
        <v-expansion-panel-content>
          <v-card-subtitle>
            <v-row align="center" justify="space-between">
              <span class='description'>
                Please select from a range of different site types
              </span>
              <v-btn
                small
                outlined
                color='#006cff'
                rounded
                class='btn-main'
                :disabled="getLoadStatus"
                :loading="getLoadStatus"
                @click="toggleLegendNodes()"
                >{{ visibleNodes.length > 0 ? "Deselect all sites" : "Enable all sites" }}</v-btn
              >
            </v-row>
          </v-card-subtitle>
          <v-radio-group v-model="visibleNodes" row multiple>
            <v-tooltip v-for="node in legendNodes"  :key="node.ID" bottom>
      <template v-slot:activator="{ on, attrs }">
        <v-radio
          :key="node.ID"
          :color="colourSettings[node.dataset].color"
          :label="node.label"
          :value="node.ID"
          style="padding: 5px"
          v-bind="attrs"
      v-on="on"
          @click="updateVisibleNodes(node.ID)"
        ></v-radio>

      </template>
      <span>{{ node.description }}</span>
    </v-tooltip>
          </v-radio-group>
        </v-expansion-panel-content>
      </v-expansion-panel>
    </v-expansion-panels>
    <v-row justify="center" no-gutters>
      <v-col sm='12' lg='6'>
        <v-expansion-panels hover flat>
          <v-expansion-panel>
            <v-expansion-panel-header class='expansion-panel-header-dense'>
              <h2>
                Site date range
              </h2>
            </v-expansion-panel-header>
            <v-expansion-panel-content>
              <v-card-subtitle>
                <v-row align="center" justify="space-between">
                  <span class='description'>
                    5 year preview range (more data maybe available at specific sites)
                  </span>
                  <v-btn
                    class='btn-main'
                    color='#006cff'
                    small
                    outlined
                    rounded
                    :disabled="getLoadStatus"
                    :loading="getLoadStatus"
                    @click="sliderDisabled = !sliderDisabled"
                    >{{ sliderDisabled ? 'Enable the slider' : 'Disable the slider' }}</v-btn
                  >
                </v-row>
              </v-card-subtitle>
              <v-row>
                <v-col cols='12'>
                  <v-range-slider
                    v-model="selectedYearRange"
                    thumb-label
                    :disabled="sliderDisabled || getLoadStatus"
                    :min="0"
                    :max="timeSliderSeries.length - 1"
                    :ticks="true"
                    :tick-labels='timeSliderSeries'
                    tick-size="1"
                    :loading="getLoadStatus"
                    :rules="[minTimeRestriction]"
                    @end="callForSiteData"
                  >
                    <template v-slot:thumb-label="props">
                        {{ timeSliderSeries[props.value] }}
                    </template>
              </v-range-slider>
                </v-col>
              </v-row>
            </v-expansion-panel-content>
          </v-expansion-panel>
        </v-expansion-panels>
      </v-col>
      <v-col sm='12' lg='6'>
        <v-expansion-panels hover flat>
          <v-expansion-panel>
            <v-expansion-panel-header class='expansion-panel-header-dense'>
              <h2>
                Site groupings
              </h2>
            </v-expansion-panel-header>
            <v-expansion-panel-content>
              <v-card-subtitle>
              <v-row align="center" justify="space-between">
                <span class='description'>
                  Choose specific local authorities and parishes to further refine the site selection
                </span>
                <v-btn
          class='btn-main'
          color='#006cff'
          small
                    outlined
                    rounded
          :disabled="getLoadStatus"
          :loading="getLoadStatus"
          @click="resetForm()"
          >Reset selection</v-btn
        >
              </v-row>
              </v-card-subtitle>
                <v-container>
                  <v-row>
                    <v-col sm="4">
                      <v-select
                        v-model="district"
                        :items="getDistricts"
                        :menu-props="{ maxHeight: '400' }"
                        label="Local authority"
                        outlined
                        hide-details
                      ></v-select>
                    </v-col>
                    <v-col sm='4'>
                      <v-select
                          v-model="town"
                          :items="getTowns"
                          :disabled='getTowns.length === 0'
                          :menu-props="{ maxHeight: '400' }"
                          label="Parish"
                          outlined
                          hide-details
                      ></v-select>
                    </v-col>
                  </v-row>
                </v-container>
            </v-expansion-panel-content>
          </v-expansion-panel>
        </v-expansion-panels>
      </v-col>
    </v-row>
  </v-container>
</template>

<script>
import { mapState, mapGetters, mapActions } from 'vuex';

export default {
  data() {
    return {
      drawer: true,
      firstLoad: true,
      states: ['A', 'B', 'C', 'D'],
      chosenState: 'A',
    };
  },
  mounted() {
    this.fetchGroupingDataset();
  },
  computed: {
    ...mapState({
      legendNodes: (state) => state.datasets.legendNodes,
      visibleNodes: (state) => state.datasets.visibleNodes,
    }),
    district: {
      get() {
        return this.$store.state.datasets.chosenDistrict;
      },
      set(v) {
        return this.$store.commit('SET_DISTRICT_FILTER', v);
      },
    },
    town: {
      get() {
        return this.$store.state.datasets.chosenTown;
      },
      set(v) {
        return this.$store.commit('SET_TOWN_FILTER', v);
      },
    },
    sliderDisabled: {
      get() {
        return this.$store.state.map.sliderDisabled;
      },
      set(v) {
        return this.$store.commit('toggle_slider', v);
      },
    },
    selectedYearRange: {
      get() {
        return this.$store.state.map.selectedYearRange;
      },
      set(value) {
        return this.$store.commit('setSelectedYearRange', value);
      },
    },
    ...mapGetters(['colourSettings', 'getLoadStatus', 'timeSliderSeries', 'getDistricts', 'getTowns', 'getProjects']),
  },
  watch: {
    timeSliderSeries(n) {
      this.$store.commit('setSelectedYearRange', [n.length - 4, n.length]);
    },
    selectedYearRange(n) {
      if (this.firstLoad) {
        this.fetchRestrictedTimeSliderSiteData(n);
        this.firstLoad = false;
      }
    },
  },
  methods: {
    ...mapActions([
      'resetForm',
      'fetchRestrictedTimeSliderSiteData',
      'setSelectedYearRange',
      'fetchGroupingDataset',
      'toggleLegendNodes',
    ]),
    updateVisibleNodes(node) {
      this.$store.commit('setVisibleNodes', node);
    },
    minTimeRestriction: (value) => {
      return value[1] - value[0] <= 4;
    },
    callForSiteData(n) {
      if (this.minTimeRestriction(n)) {
        this.fetchRestrictedTimeSliderSiteData(n);
      }
    },
  },
};
</script>

<style scoped lang="scss">
.action-btn {
  color: #fff;
  margin-left: 10px;
}
</style>
